import React, { useEffect, useRef, useState } from "react";
import NormalAlert from "../../../Alerts/NormalAlert";
import {
  GetInterviewDetails,
  ScheduleInterview,
} from "../../InterviewerAssessment/Services/InterviewService";
import { useParams, useNavigate } from "react-router-dom";
import { CalendarCheck, User, Briefcase, CalendarPlus } from "lucide-react";

const getLocalMinDateTime = () => {
  const now = new Date();
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
  return now.toISOString().slice(0, 16);
};

const InterviewSchedule = () => {
  const navigate = useNavigate();
  const { InterviewUID } = useParams();

  const [minDateTime, setMinDateTime] = useState(getLocalMinDateTime());
  const timerRef = useRef(null);
  const [alert, setAlert] = useState(null);

  const [candidateName, setCandidateName] = useState("");
  const [jobTitle, setJobTitle] = useState("");

  useEffect(() => {
    setMinDateTime(getLocalMinDateTime());

    const fetchInterviewDetails = async () => {
      try {
        const response = await GetInterviewDetails(InterviewUID);
        setCandidateName(response.data.candidateName || "");
        setJobTitle(response.data.jobTitle || "");
      } catch (error) {
        setAlert({
          type: "fail",
          message: "Failed to load interview details",
        });
        navigate(`/Interview-Scheduling-Request/${InterviewUID}`, {
          replace: true,
        });
      }
    };

    if (InterviewUID) {
      fetchInterviewDetails();
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [InterviewUID]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData);

    const now = new Date();
    const selectedDate = new Date(data.interviewDate);

    if (selectedDate < now) {
      setAlert({
        type: "fail",
        message: "Date cannot be in the past",
      });
      return;
    }

    try {
      const payload = {
        interviewUID: InterviewUID,
        interviewDateTime: data.interviewDate,
      };

      const response = await ScheduleInterview(payload);

      if (response.data?.status?.toLowerCase() === "success") {
        setAlert({
          type: "success",
          message:
            response.data?.message || "Interview scheduled successfully!",
        });
      } else {
        setAlert({
          type: "fail",
          message: response.data?.message || "Failed to schedule interview",
        });
      }
    } catch {
      setAlert({
        type: "fail",
        message: "Something went wrong while scheduling",
      });
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4 py-10">
      <div className="w-full max-w-lg bg-white rounded-3xl shadow-xl p-10">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto rounded-full bg-blue-500 flex items-center justify-center shadow-md">
            <CalendarCheck size={28} className="text-white" />
          </div>

          <h2 className="text-3xl font-bold text-gray-800 mt-5">
            Schedule Interview
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Candidate Name */}
          <div>
            <label className="flex items-center gap-2 text-gray-700 font-medium mb-2">
              <User size={18} />
              Candidate Name
            </label>

            <input
              type="text"
              value={candidateName}
              disabled
              name="candidateName"
              className="w-full bg-gray-100 border border-gray-200 rounded-xl px-4 py-3 focus:outline-none"
            />
          </div>

          {/* Job Title */}
          <div>
            <label className="flex items-center gap-2 text-gray-700 font-medium mb-2">
              <Briefcase size={18} />
              Job Title
            </label>

            <input
              type="text"
              value={jobTitle}
              disabled
              name="jobTitle"
              className="w-full bg-gray-100 border border-gray-200 rounded-xl px-4 py-3 focus:outline-none"
            />
          </div>

          {/* Interview Date */}
          <div>
            <label className="flex items-center gap-2 text-gray-700 font-medium mb-2">
              <CalendarPlus size={18} />
              Interview Date & Time
            </label>

            <input
              type="datetime-local"
              required
              name="interviewDate"
              min={minDateTime}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:outline-none"
            />
          </div>

          {/* Button */}
          <button
            type="submit"
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-white font-semibold bg-gradient-to-r from-blue-500 to-blue-700 hover:opacity-95 transition"
          >
            <CalendarPlus size={18} />
            SCHEDULE INTERVIEW
          </button>
        </form>
      </div>

      {alert && (
        <NormalAlert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}
    </div>
  );
};

export default InterviewSchedule;
